import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  TrackByFunction,
} from '@angular/core';
import { UserChatMessage } from '../chat.model';
import { ComponentChanges } from '@bcl/common-ui';

const lastMessageReadDelayInMs = 1000;
@Component({
  selector: 'pc-chat-preview',
  templateUrl: './chat-preview.component.html',
  styleUrls: ['./chat-preview.component.scss'],
})
export class ChatPreviewComponent implements OnChanges, OnDestroy {
  @Input()
  public messages: UserChatMessage[] = [];
  @Input()
  public currentUserId: string | undefined;

  @Output()
  public messageInitialized = new EventEmitter<void>();
  @Output()
  public lastMessageRead = new EventEmitter<UserChatMessage>();

  private lastObservedMessage: Element | null = null;
  private observer: IntersectionObserver | null = null;
  private timer: any = null;

  constructor(private elRef: ElementRef, private ngZone: NgZone) {}

  public ngOnChanges(changes: ComponentChanges<ChatPreviewComponent>): void {
    if (changes.messages) {
      this.observeLastMessage();
    }
  }

  public readonly trackByTimestamp: TrackByFunction<UserChatMessage> = (
    _index: number,
    message: UserChatMessage
  ) => message.timestamp;

  public ngOnDestroy(): void {
    this.observer?.disconnect();
    if (this.timer) {
      clearTimeout(this.timer);
    }
  }

  private observeLastMessage() {
    this.ngZone.runOutsideAngular(() => {
      setTimeout(() => {
        const container = this.elRef.nativeElement.querySelector('.container');
        if (!container) {
          return;
        }
        const messages = container.querySelectorAll('pc-chat-message');
        if (!messages.length) {
          return;
        }
        const lastMessage = messages[messages.length - 1];
        if (this.lastObservedMessage === lastMessage) {
          return;
        }
        if (this.observer && this.lastObservedMessage) {
          this.observer.unobserve(this.lastObservedMessage);
        }
        this.lastObservedMessage = lastMessage;
        this.observer = new IntersectionObserver(
          (entries) => {
            const entry = entries[0];
            if (entry.isIntersecting) {
              this.timer = setTimeout(() => {
                this.ngZone.run(() => {
                  this.onLastMessageVisible();
                });
              }, lastMessageReadDelayInMs);
            } else {
              clearTimeout(this.timer);
            }
          },
          { threshold: 0.8 }
        );
        this.observer.observe(lastMessage);
      }, 200);
    });
  }

  private onLastMessageVisible() {
    this.lastMessageRead.emit(this.messages[this.messages.length - 1]);
  }
}
